import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useAdditionalProfiles } from "@/hooks/useAdditionalProfiles";
import { useTranslation } from "react-i18next";

interface EditManagedProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    birthdate?: string;
    relationship_type?: string;
  };
}

export const EditManagedProfileModal = ({ isOpen, onClose, profile }: EditManagedProfileModalProps) => {
  const { t } = useTranslation('common');
  const { updateProfile, isUpdating } = useAdditionalProfiles();
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    birthdate: undefined as Date | undefined,
    relationshipType: 'managed'
  });

  // Update form data when profile changes
  useEffect(() => {
    if (profile && isOpen) {
      setFormData({
        username: profile.username || '',
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        birthdate: profile.birthdate ? new Date(profile.birthdate) : undefined,
        relationshipType: profile.relationship_type || 'managed'
      });
    }
  }, [profile, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.firstName) {
      return;
    }

    try {
      await updateProfile(profile.id, {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthdate: formData.birthdate ? format(formData.birthdate, 'yyyy-MM-dd') : undefined,
        relationshipType: formData.relationshipType as 'gift' | 'memorial' | 'managed'
      });
      
      onClose();
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    if (profile) {
      setFormData({
        username: profile.username || '',
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        birthdate: profile.birthdate ? new Date(profile.birthdate) : undefined,
        relationshipType: profile.relationship_type || 'managed'
      });
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update the profile information for this timeline.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('profile.firstName', 'First Name')} *</Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder={t('profile.firstNamePlaceholder', 'Enter first name')}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('profile.lastName', 'Last Name')}</Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder={t('profile.lastNamePlaceholder', 'Enter last name')}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="username">{t('profile.username', 'Username')} *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase() }))}
              placeholder={t('profile.usernamePlaceholder', 'Enter username')}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label>{t('profile.birthdate', 'Birthdate')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.birthdate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.birthdate ? format(formData.birthdate, "PPP") : t('profile.selectBirthdate', 'Select birthdate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.birthdate}
                  onSelect={(date) => setFormData(prev => ({ ...prev, birthdate: date }))}
                  disabled={(date) =>
                    date > new Date() || date < new Date("1900-01-01")
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="space-y-2">
            <Label>{t('profile.relationshipType', 'Relationship Type')}</Label>
            <Select
              value={formData.relationshipType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, relationshipType: value as any }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gift">{t('profile.giftTimeline', 'Gift Timeline')}</SelectItem>
                <SelectItem value="memorial">{t('profile.memorialTimeline', 'Memorial Timeline')}</SelectItem>
                <SelectItem value="managed">{t('profile.managedTimeline', 'Managed Timeline')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isUpdating || !formData.username || !formData.firstName} className="flex-1">
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};